/**
 * Media Helpers
 * Shared utilities for working with media attachments (images, videos)
 * Works with both MediaAttachment and FileUpload types
 */

import { extractSourceInfoFromUrl } from './video.utils'
import type { MediaItem, SourceInfo } from '../types'

export type { MediaItem } from '../types'

/**
 * True when `value` looks like a DRM license id (opaque id), not a URL/path or filename
 * mistakenly stored in `licenseId` (e.g. `https://…`, `blob:…`, paths, or `*.jpg` / `*.mp4`).
 */
export function isValidGalleryLicenseId(value?: string): boolean {
  if (!value) return false
  return (
    !value.startsWith('http') && !value.startsWith('blob:') && !value.includes('/') && !value.match(/\.(jpg|jpeg|png|gif|webp|mp4|mov)$/i)
  )
}

/**
 * Get the appropriate media URL from a media item.
 * Priority: html (embed) > thumbnail_url > url
 *
 * @param item - Media item (MediaAttachment or FileUpload)
 * @returns Media URL string
 *
 * @example
 * getMediaUrl({ html: '<iframe src="https://youtube.com/embed/abc"></iframe>', url: 'fallback.jpg' })
 * // Returns: '<iframe src="https://youtube.com/embed/abc"></iframe>'
 *
 * @example
 * getMediaUrl({ thumbnail_url: 'thumb.jpg', url: 'full.jpg' })
 * // Returns: 'thumb.jpg'
 *
 * @example
 * getMediaUrl({ url: 'https://storage.googleapis.com/bucket/video.mp4' })
 * // Returns: 'https://storage.googleapis.com/bucket/video.mp4'
 */
export function getMediaUrl(item: MediaItem): string {
  return item.html || item.thumbnail_url || item.url || ''
}

/**
 * Check if a media item is a video
 *
 * @param item - Media item (MediaAttachment or FileUpload)
 * @returns True if the item is a video
 *
 * @example
 * isVideo({ type: 'video/mp4', url: 'video.mp4' })
 * // Returns: true
 *
 * @example
 * isVideo({ type: 'image/jpeg', url: 'photo.jpg' })
 * // Returns: false
 */
export function isVideo(item: MediaItem): boolean {
  return item.type?.includes('video') || false
}

/**
 * Get or generate sourceInfo for a media item.
 * Returns existing sourceInfo if available, otherwise generates it for bucket URL videos.
 *
 * @param item - Media item (MediaAttachment or FileUpload)
 * @returns SourceInfo if available or can be generated, undefined otherwise
 *
 * @example
 * // Item already has sourceInfo → returned as-is
 * getSourceInfo({ sourceInfo: { appSource: 'courses', bucket: 'b', filePath: 'f' }, type: 'video/mp4' })
 * // Returns: { appSource: 'courses', bucket: 'b', filePath: 'f' }
 *
 * @example
 * // Video without sourceInfo → auto-generated from URL
 * getSourceInfo({ type: 'video/mp4', url: 'https://storage.googleapis.com/revex-membership-production/video.mp4' })
 * // Returns: { appSource: 'courses', bucket: 'revex-membership-production', filePath: 'video.mp4' }
 *
 * @example
 * // Image item → no sourceInfo needed
 * getSourceInfo({ type: 'image/jpeg', url: 'photo.jpg' })
 * // Returns: undefined
 */
export function getSourceInfo(item: MediaItem): SourceInfo | undefined {
  // Return existing sourceInfo if available
  if (item.sourceInfo) return item.sourceInfo

  // Generate sourceInfo for bucket URL videos without licenseId
  if (isVideo(item) && !item.licenseId) {
    const videoUrl = getMediaUrl(item)
    return extractSourceInfoFromUrl(videoUrl) || undefined
  }

  return undefined
}
